import { FirebaseStorageService, FileType, UploadResult, FileMetadata } from './storage';
import { PersonalInfoService } from './services';

// File validation constants
export const FILE_CONSTRAINTS = {
  PROFILE_IMAGE: {
    allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxSizeMB: 5,
    dimensions: { maxWidth: 2000, maxHeight: 2000 }
  },
  DOCUMENTS: {
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
    maxSizeMB: 10
  },
  APPLICATION_IMAGES: {
    allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxSizeMB: 8
  },
  APPOINTMENT_ATTACHMENTS: {
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    maxSizeMB: 15
  }
};

export class ProfileImageService {
  static async uploadProfileImage(file: File, userId: string): Promise<UploadResult> {
    // Validate file
    const validation = FirebaseStorageService.validateFile(
      file, 
      FILE_CONSTRAINTS.PROFILE_IMAGE.allowedTypes, 
      FILE_CONSTRAINTS.PROFILE_IMAGE.maxSizeMB
    );
    
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Generate thumbnail
    const thumbnail = await FirebaseStorageService.generateThumbnail(file);
    
    // Upload original image
    const originalResult = await FirebaseStorageService.uploadFile(
      file, 
      userId, 
      'profile', 
      'images',
      { originalName: file.name }
    );

    // Upload thumbnail
    const thumbnailResult = await FirebaseStorageService.uploadFile(
      thumbnail, 
      userId, 
      'profile', 
      'thumbnails',
      { originalName: file.name, isThumbnail: 'true' }
    );

    // Update user profile with image URLs
    await PersonalInfoService.updateProfileImage(userId, {
      profileImageUrl: originalResult.url,
      profileImageThumbnailUrl: thumbnailResult.url,
      profileImagePath: originalResult.path,
      profileImageThumbnailPath: thumbnailResult.path
    });

    return originalResult;
  }

  static async deleteProfileImage(userId: string, imagePath: string, thumbnailPath: string): Promise<void> {
    try {
      // Delete both original and thumbnail
      await Promise.all([
        FirebaseStorageService.deleteFile(imagePath),
        FirebaseStorageService.deleteFile(thumbnailPath)
      ]);

      // Update user profile to remove image URLs
      await PersonalInfoService.updateProfileImage(userId, {
        profileImageUrl: null,
        profileImageThumbnailUrl: null,
        profileImagePath: null,
        profileImageThumbnailPath: null
      });
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw error;
    }
  }

  static async getUserProfileImages(userId: string): Promise<FileMetadata[]> {
    return FirebaseStorageService.listUserFiles(userId, 'profile');
  }
}

export class DocumentUploadService {
  static async uploadDocument(file: File, userId: string, documentType: string): Promise<UploadResult> {
    // Validate file
    const validation = FirebaseStorageService.validateFile(
      file, 
      FILE_CONSTRAINTS.DOCUMENTS.allowedTypes, 
      FILE_CONSTRAINTS.DOCUMENTS.maxSizeMB
    );
    
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return FirebaseStorageService.uploadFile(
      file, 
      userId, 
      'document', 
      documentType,
      { 
        documentType,
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    );
  }

  static async uploadMultipleDocuments(files: File[], userId: string, documentType: string): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => 
      this.uploadDocument(file, userId, documentType)
    );
    
    return Promise.all(uploadPromises);
  }

  static async getUserDocuments(userId: string, documentType?: string): Promise<FileMetadata[]> {
    const allDocuments = await FirebaseStorageService.listUserFiles(userId, 'document');
    
    if (documentType) {
      return allDocuments.filter(doc => 
        doc.path.includes(`/${documentType}/`)
      );
    }
    
    return allDocuments;
  }

  static async deleteDocument(userId: string, documentPath: string): Promise<void> {
    await FirebaseStorageService.deleteFile(documentPath);
  }
}

export class ApplicationImageService {
  static async uploadApplicationImage(file: File, userId: string, applicationId: string): Promise<UploadResult> {
    // Validate file
    const validation = FirebaseStorageService.validateFile(
      file, 
      FILE_CONSTRAINTS.APPLICATION_IMAGES.allowedTypes, 
      FILE_CONSTRAINTS.APPLICATION_IMAGES.maxSizeMB
    );
    
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return FirebaseStorageService.uploadFile(
      file, 
      userId, 
      'application', 
      applicationId,
      { 
        applicationId,
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    );
  }

  static async uploadApplicationImages(files: File[], userId: string, applicationId: string): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => 
      this.uploadApplicationImage(file, userId, applicationId)
    );
    
    return Promise.all(uploadPromises);
  }

  static async getApplicationImages(userId: string, applicationId: string): Promise<FileMetadata[]> {
    const allImages = await FirebaseStorageService.listUserFiles(userId, 'application');
    return allImages.filter(img => img.path.includes(`/${applicationId}/`));
  }

  static async deleteApplicationImage(userId: string, imagePath: string): Promise<void> {
    await FirebaseStorageService.deleteFile(imagePath);
  }
}

export class AppointmentAttachmentService {
  static async uploadAttachment(file: File, userId: string, appointmentId: string): Promise<UploadResult> {
    // Validate file
    const validation = FirebaseStorageService.validateFile(
      file, 
      FILE_CONSTRAINTS.APPOINTMENT_ATTACHMENTS.allowedTypes, 
      FILE_CONSTRAINTS.APPOINTMENT_ATTACHMENTS.maxSizeMB
    );
    
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return FirebaseStorageService.uploadFile(
      file, 
      userId, 
      'appointment', 
      appointmentId,
      { 
        appointmentId,
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    );
  }

  static async uploadMultipleAttachments(files: File[], userId: string, appointmentId: string): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => 
      this.uploadAttachment(file, userId, appointmentId)
    );
    
    return Promise.all(uploadPromises);
  }

  static async getAppointmentAttachments(userId: string, appointmentId: string): Promise<FileMetadata[]> {
    const allAttachments = await FirebaseStorageService.listUserFiles(userId, 'appointment');
    return allAttachments.filter(attachment => attachment.path.includes(`/${appointmentId}/`));
  }

  static async deleteAppointmentAttachment(userId: string, attachmentPath: string): Promise<void> {
    await FirebaseStorageService.deleteFile(attachmentPath);
  }
}

// General file management service
export class FileManagementService {
  static async getUserAllFiles(userId: string): Promise<{ [key in FileType]: FileMetadata[] }> {
    const [profileFiles, documentFiles, applicationFiles, appointmentFiles] = await Promise.all([
      FirebaseStorageService.listUserFiles(userId, 'profile'),
      FirebaseStorageService.listUserFiles(userId, 'document'),
      FirebaseStorageService.listUserFiles(userId, 'application'),
      FirebaseStorageService.listUserFiles(userId, 'appointment')
    ]);

    return {
      profile: profileFiles,
      document: documentFiles,
      application: applicationFiles,
      appointment: appointmentFiles,
      general: [] // Can be used for general files
    };
  }

  static async getStorageUsage(userId: string): Promise<{
    totalFiles: number;
    totalSize: number;
    byCategory: { [key in FileType]: { count: number; size: number } };
  }> {
    const allFiles = await this.getUserAllFiles(userId);
    
    let totalFiles = 0;
    let totalSize = 0;
    const byCategory: { [key in FileType]: { count: number; size: number } } = {
      profile: { count: 0, size: 0 },
      document: { count: 0, size: 0 },
      application: { count: 0, size: 0 },
      appointment: { count: 0, size: 0 },
      general: { count: 0, size: 0 }
    };

    Object.entries(allFiles).forEach(([category, files]) => {
      const categoryKey = category as FileType;
      const count = files.length;
      const size = files.reduce((sum, file) => sum + file.size, 0);
      
      byCategory[categoryKey] = { count, size };
      totalFiles += count;
      totalSize += size;
    });

    return {
      totalFiles,
      totalSize,
      byCategory
    };
  }

  static async cleanupUserFiles(userId: string): Promise<void> {
    const allFiles = await this.getUserAllFiles(userId);
    const deletePromises: Promise<void>[] = [];

    Object.values(allFiles).forEach(files => {
      files.forEach(file => {
        deletePromises.push(FirebaseStorageService.deleteFile(file.path));
      });
    });

    await Promise.all(deletePromises);
  }
}
